import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useAuth } from '../../../context/AuthContext';
import { dictionary, Language } from '../../../utils/dictionary';
import { FunnelSection, FunnelConfig, SalesAngle } from '../../../types/onboarding';
import { Toggle } from '../ui/Toggle';
import { GripVertical, Pencil, Image, HelpCircle, ShoppingCart, Shield, Settings, Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { Clock, Users, Zap, Leaf, Sparkles } from 'lucide-react';
import Loader from '../../common/Loader';
import { SectionTemplate } from '../../../services/funnels';
import type { AdStyle } from '../../../feature/ads/cretives/components/modals/steps/adStudio/types';
import { LazyImage } from '../../ui/lazy-image';

const sectionIcons: Record<string, React.ComponentType<any>> = {
  'image': Image,
  'help-circle': HelpCircle,
  'shopping-cart': ShoppingCart,
  'shield': Shield,
};

// Word-level translation map for template names
const TEMPLATE_WORD_TRANSLATIONS: Record<string, Record<string, string>> = {
  // Spanish words → { en, pt }
  'tecnologia': { en: 'Technology', pt: 'Tecnologia', es: 'Tecnología' },
  'neon': { en: 'Neon', pt: 'Neon', es: 'Neón' },
  'verde': { en: 'Green', pt: 'Verde', es: 'Verde' },
  'azul': { en: 'Blue', pt: 'Azul', es: 'Azul' },
  'rojo': { en: 'Red', pt: 'Vermelho', es: 'Rojo' },
  'negro': { en: 'Black', pt: 'Preto', es: 'Negro' },
  'blanco': { en: 'White', pt: 'Branco', es: 'Blanco' },
  'dorado': { en: 'Gold', pt: 'Dourado', es: 'Dorado' },
  'elegante': { en: 'Elegant', pt: 'Elegante', es: 'Elegante' },
  'moderno': { en: 'Modern', pt: 'Moderno', es: 'Moderno' },
  'clasico': { en: 'Classic', pt: 'Clássico', es: 'Clásico' },
  'minimalista': { en: 'Minimalist', pt: 'Minimalista', es: 'Minimalista' },
  'profesional': { en: 'Professional', pt: 'Profissional', es: 'Profesional' },
  'premium': { en: 'Premium', pt: 'Premium', es: 'Premium' },
  'natural': { en: 'Natural', pt: 'Natural', es: 'Natural' },
  'salud': { en: 'Health', pt: 'Saúde', es: 'Salud' },
  'belleza': { en: 'Beauty', pt: 'Beleza', es: 'Belleza' },
  'fitness': { en: 'Fitness', pt: 'Fitness', es: 'Fitness' },
  'deportivo': { en: 'Sport', pt: 'Esportivo', es: 'Deportivo' },
  'hogar': { en: 'Home', pt: 'Casa', es: 'Hogar' },
  'cocina': { en: 'Kitchen', pt: 'Cozinha', es: 'Cocina' },
  'moda': { en: 'Fashion', pt: 'Moda', es: 'Moda' },
  'lujo': { en: 'Luxury', pt: 'Luxo', es: 'Lujo' },
  'agresivo': { en: 'Aggressive', pt: 'Agressivo', es: 'Agresivo' },
  'suave': { en: 'Soft', pt: 'Suave', es: 'Suave' },
  'oscuro': { en: 'Dark', pt: 'Escuro', es: 'Oscuro' },
  'claro': { en: 'Light', pt: 'Claro', es: 'Claro' },
  'vibrante': { en: 'Vibrant', pt: 'Vibrante', es: 'Vibrante' },
  'secadora': { en: 'Dryer', pt: 'Secadora', es: 'Secadora' },
  'maquillaje': { en: 'Makeup', pt: 'Maquiagem', es: 'Maquillaje' },
  'rosa': { en: 'Pink', pt: 'Rosa', es: 'Rosa' },
};

/**
 * Formats a template name from API slug format to display format.
 * e.g. "preview-tecnologia-neon-verde" → "Tecnología Neón Verde" (es)
 *      "preview-tecnologia-neon-verde" → "Technology Neon Green" (en)
 */
const formatTemplateName = (name: string, lang: string): string => {
  // Remove "preview-" prefix
  let cleaned = name.replace(/^preview-/i, '');
  // Split by hyphens into words
  const words = cleaned.split('-');
  // Translate each word if available, otherwise capitalize
  const langKey = lang === 'en' ? 'en' : lang === 'pt' ? 'pt' : 'es';
  const formatted = words.map(word => {
    const translation = TEMPLATE_WORD_TRANSLATIONS[word.toLowerCase()];
    if (translation && translation[langKey]) {
      return translation[langKey];
    }
    // Fallback: capitalize first letter
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
  return formatted.join(' ');
};

// Fallback mock templates in case API returns empty
const FALLBACK_TEMPLATES = [
  { id: 'modern', color: 'from-[#6366F1] to-[#8B5CF6]', name: 'Moderno', description: 'Limpio y profesional' },
  { id: 'aggressive', color: 'from-[#EF4444] to-[#F97316]', name: 'Agresivo', description: 'Alto impacto visual' },
  { id: 'elegant', color: 'from-[#1E293B] to-[#475569]', name: 'Elegante', description: 'Sofisticado y premium' },
];

export function StepFunnelConfig({
  onComplete,
  onBack,
}: {
  onComplete?: () => void;
  onBack?: () => void;
} = {}) {
  const { language } = useAuth();
  const { multiFunnelData, updateFunnelConfig, salesAngles, sectionTemplates, isFetchingTemplates, fetchSectionTemplates, setFunnelDesign, designRecommendation, isFetchingDesignRecommendation, salesAngleRecommendation, isFetchingSalesAngleRecommendation, adTemplates, isFetchingAdTemplates, fetchAdTemplates, toggleFunnelAdTemplate } = useOnboardingStore();
  const [hasAutoApplied, setHasAutoApplied] = useState(false);

  useEffect(() => {
    fetchSectionTemplates();
    fetchAdTemplates();
  }, []);

  // Auto-apply design recommendation when available
  useEffect(() => {
    if (hasAutoApplied || isFetchingDesignRecommendation || !designRecommendation || isFetchingTemplates) return;
    if (sectionTemplates.length === 0) return;

    // Find the template that matches the recommended type
    const recommendedType = designRecommendation.recommended_template_type;
    const matchingTemplate = sectionTemplates.find(t => t.type === recommendedType);

    if (matchingTemplate) {
      // Auto-apply to all funnels that don't have a design yet
      // Pass recommended sections so they get properly enabled/ordered
      multiFunnelData.funnels.forEach((funnel, index) => {
        if (!funnel.designTemplate) {
          setFunnelDesign(
            index,
            matchingTemplate.id.toString(),
            matchingTemplate.type,
            designRecommendation.recommended_sections
          );
        }
      });
    }

    setHasAutoApplied(true);
  }, [designRecommendation, sectionTemplates, isFetchingTemplates, isFetchingDesignRecommendation, hasAutoApplied, multiFunnelData.funnels]);

  // Auto-select recommended angle for all funnels
  const [hasAutoAppliedAngle, setHasAutoAppliedAngle] = useState(false);
  useEffect(() => {
    if (hasAutoAppliedAngle || isFetchingSalesAngleRecommendation || !salesAngleRecommendation) return;
    if (multiFunnelData.funnels.length === 0) return;

    const matchingAngle = salesAngles.find(
      (a) => a.id === salesAngleRecommendation.selected_angle || a.title === salesAngleRecommendation.selected_angle
    );

    if (matchingAngle) {
      multiFunnelData.funnels.forEach((funnel, index) => {
        if (funnel.selectedAngles.length === 0) {
          updateFunnelConfig(index, {
            selectedAngles: [{ name: matchingAngle.id, description: matchingAngle.description }],
          });
        }
      });
    }

    setHasAutoAppliedAngle(true);
  }, [salesAngleRecommendation, isFetchingSalesAngleRecommendation, hasAutoAppliedAngle, multiFunnelData.funnels, salesAngles]);

  const [activeFunnelIndex, setActiveFunnelIndex] = useState(0);
  const [activeSubStep, setActiveSubStep] = useState<1 | 2 | 3>(1);

  const funnels = multiFunnelData.funnels;
  const activeFunnel = funnels[activeFunnelIndex];
  const totalFunnels = funnels.length;

  if (!activeFunnel) return null;

  const handleToggleAngle = (angleId: string) => {
    const currentAngles = activeFunnel.selectedAngles;
    const isSelected = currentAngles.some(a => a.name === angleId);
    let newAngles: { name: string; description: string }[] = [];

    if (isSelected) {
      newAngles = [];
    } else {
      const angle = salesAngles.find(a => a.id === angleId);
      newAngles = [{ name: angleId, description: angle?.description || angleId }];
    }

    updateFunnelConfig(activeFunnelIndex, { selectedAngles: newAngles });
  };

  const handleSelectDesign = (templateId: string, templateType: string) => {
    setFunnelDesign(activeFunnelIndex, templateId, templateType, designRecommendation?.recommended_sections);
  };

  const handleToggleAdTemplate = (templateId: string) => {
    toggleFunnelAdTemplate(activeFunnelIndex, templateId);
  };

  // Angle editing: update description for a selected angle
  const handleUpdateAngleDescription = (angleId: string, newDescription: string) => {
    const updated = activeFunnel.selectedAngles.map(
      (a: { name: string; description: string }) =>
        a.name === angleId ? { ...a, description: newDescription } : a
    );
    updateFunnelConfig(activeFunnelIndex, { selectedAngles: updated });
    // Persist to global salesAngles so edits persist when switching funnels
    const store = useOnboardingStore.getState();
    store.setSalesAngles(store.salesAngles.map((a: SalesAngle) =>
      a.id === angleId ? { ...a, description: newDescription } : a
    ));
  };

  // Custom angle: create a user-defined angle
  const handleSelectCustomAngle = (name: string, description: string) => {
    updateFunnelConfig(activeFunnelIndex, {
      selectedAngles: [{ name, description }],
    });
  };

  // Wizard navigation
  const handleNext = () => {
    if (activeSubStep === 1) {
      if (!activeFunnel.designTemplate) return;
      setActiveSubStep(2);
    } else if (activeSubStep === 2) {
      if (activeFunnel.selectedAngles.length === 0) return;
      setActiveSubStep(3);
    } else if (activeSubStep === 3) {
      if (activeFunnelIndex < totalFunnels - 1) {
        setActiveFunnelIndex(activeFunnelIndex + 1);
        setActiveSubStep(1);
      } else {
        onComplete?.();
      }
    }
  };

  const handleBack = () => {
    if (activeSubStep > 1) {
      setActiveSubStep((activeSubStep - 1) as 1 | 2 | 3);
    } else if (activeFunnelIndex > 0) {
      setActiveFunnelIndex(activeFunnelIndex - 1);
      setActiveSubStep(3);
    } else {
      onBack?.();
    }
  };

  // Determine next button label
  const getNextLabel = () => {
    if (activeSubStep === 3 && activeFunnelIndex < totalFunnels - 1) {
      return dictionary["next-funnel"]?.[language] ?? "Siguiente embudo";
    }
    return dictionary["next"]?.[language] ?? dictionary["continue"]?.[language] ?? "Siguiente";
  };

  return (
    <div className="space-y-4">
      {/* Wizard Header: Funnel pill + Stepper */}
      <FunnelWizardHeader
        activeFunnelIndex={activeFunnelIndex}
        totalFunnels={totalFunnels}
        activeSubStep={activeSubStep}
        language={language}
      />

      {/* Sub-step content — kept mounted with CSS hidden to preserve images/state */}
      <div className="min-h-[480px]">
        <div className={activeSubStep !== 1 ? 'hidden' : undefined}>
          <DesignTab
            activeFunnel={activeFunnel}
            onSelectDesign={handleSelectDesign}
            language={language}
            sectionTemplates={sectionTemplates}
            isFetchingTemplates={isFetchingTemplates}
          />
        </div>

        <div className={activeSubStep !== 2 ? 'hidden' : undefined}>
          <AngleTab
            activeFunnel={activeFunnel}
            salesAngles={salesAngles}
            handleToggleAngle={handleToggleAngle}
            onUpdateAngleDescription={handleUpdateAngleDescription}
            onSelectCustomAngle={handleSelectCustomAngle}
            language={language}
            isFetchingSalesAngleRecommendation={isFetchingSalesAngleRecommendation}
          />
        </div>

        <div className={activeSubStep !== 3 ? 'hidden' : undefined}>
          <AdsTab
            activeFunnel={activeFunnel}
            language={language}
            adTemplates={adTemplates}
            isFetchingAdTemplates={isFetchingAdTemplates}
            onToggleTemplate={handleToggleAdTemplate}
          />
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between items-center pt-2">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {dictionary["back"]?.[language] ?? "Atrás"}
        </button>
        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium bg-[#3E3EFF] hover:bg-[#2d3a7f] transition-all"
        >
          {getNextLabel()}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Funnel Wizard Header ─────────────────────────────────────
function FunnelWizardHeader({
  activeFunnelIndex,
  totalFunnels,
  activeSubStep,
  language,
}: {
  activeFunnelIndex: number;
  totalFunnels: number;
  activeSubStep: 1 | 2 | 3;
  language: Language;
}) {
  const funnelLabel = dictionary["funnel-label"]?.[language] ?? "Embudo";

  return (
    <div className="flex items-center justify-between mb-2">
      {/* Funnel indicator pill */}
      <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#F1F5F9] rounded-full">
        <span className="text-sm font-semibold text-gray-700">
          {funnelLabel}
        </span>
        <span className="text-sm font-bold text-[#3E3EFF]">
          {activeFunnelIndex + 1}/{totalFunnels}
        </span>
      </div>

      {/* 3-step horizontal stepper */}
      <div className="flex items-center gap-0">
        {[1, 2, 3].map((stepNum, index) => {
          const isActive = stepNum === activeSubStep;
          const isCompleted = stepNum < activeSubStep;
          return (
            <div key={stepNum} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                isActive
                  ? 'bg-[#3E3EFF] text-white shadow-md shadow-indigo-200'
                  : isCompleted
                    ? 'bg-[#3E3EFF]/10 text-[#3E3EFF]'
                    : 'bg-[#F1F5F9] text-gray-400'
              }`}>
                {isCompleted ? (
                  <Check className="w-4 h-4" />
                ) : stepNum}
              </div>
              {index < 2 && (
                <div className={`w-16 h-0.5 mx-1 transition-colors ${
                  stepNum < activeSubStep ? 'bg-[#3E3EFF]/30' : 'bg-[#E2E8F0]'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Design Tab ───────────────────────────────────────────────
function DesignTab({
  activeFunnel,
  onSelectDesign,
  language,
  sectionTemplates,
  isFetchingTemplates,
}: {
  activeFunnel: any;
  onSelectDesign: (templateId: string, templateType: string) => void;
  language: Language;
  sectionTemplates: SectionTemplate[];
  isFetchingTemplates: boolean;
}) {
  const hasTemplates = sectionTemplates.length > 0;
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div>
      <h2 className="text-lg font-bold text-gray-900 mb-4">
        {dictionary["select-design-template"]?.[language] ?? "Selecciona una plantilla para la página de producto"}
      </h2>

      {isFetchingTemplates ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={36} />
        </div>
      ) : hasTemplates ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-[550px] overflow-y-auto pr-1 py-1">
          {sectionTemplates.map((template) => {
            const templateId = template.id.toString();
            const isSelected = activeFunnel.designTemplate === templateId;
            const isHovered = hoveredId === templateId;
            const description = (template.metadata as any)?.['sub-title'] || template.type;

            return (
              <button
                key={template.id}
                onClick={() => onSelectDesign(templateId, template.type)}
                onMouseEnter={() => setHoveredId(templateId)}
                onMouseLeave={() => setHoveredId(null)}
                className={`relative w-full rounded-2xl overflow-hidden border-2 transition-shadow duration-300 ${
                  isSelected
                    ? 'border-[#3E3EFF] shadow-lg'
                    : 'border-transparent hover:border-gray-200'
                }`}
              >
                  {template.url ? (
                    <div className="w-full aspect-[3/4] bg-gray-100 overflow-hidden">
                      <img
                        src={template.url}
                        alt={template.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: isHovered ? 'center bottom' : 'center top',
                          transition: isHovered
                            ? 'object-position 15s linear'
                            : 'object-position 0.5s ease-out',
                        }}
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-[3/4] bg-gradient-to-b from-[#6366F1] to-[#8B5CF6] p-3 flex flex-col justify-between">
                      <div className="space-y-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 mx-auto" />
                        <div className="h-1.5 bg-white/30 rounded-full w-3/4 mx-auto" />
                        <div className="h-1.5 bg-white/30 rounded-full w-1/2 mx-auto" />
                      </div>
                      <div className="space-y-1">
                        <div className="h-3 bg-white/20 rounded-md" />
                        <div className="h-3 bg-white/20 rounded-md" />
                      </div>
                    </div>
                  )}
                  {/* Title + description */}
                  <div className="px-2 py-2 bg-white">
                    <p className="text-xs font-bold text-gray-800 truncate">{formatTemplateName(template.name, language)}</p>
                    <p className="text-[10px] text-gray-400 truncate">{description}</p>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-[#3E3EFF] rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
              </button>
            );
          })}
        </div>
      ) : (
        /* Fallback: show mock templates if API returned nothing */
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {FALLBACK_TEMPLATES.map((template) => (
            <button
              key={template.id}
              onClick={() => onSelectDesign(template.id, template.id)}
              className={`relative rounded-2xl overflow-hidden border-2 transition-all ${
                activeFunnel.designTemplate === template.id
                  ? 'border-[#3E3EFF] shadow-lg'
                  : 'border-transparent hover:border-gray-200'
              }`}
            >
              <div className={`w-full aspect-[3/4] bg-gradient-to-b ${template.color} p-3 flex flex-col justify-between`}>
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 mx-auto" />
                  <div className="h-1.5 bg-white/30 rounded-full w-3/4 mx-auto" />
                  <div className="h-1.5 bg-white/30 rounded-full w-1/2 mx-auto" />
                </div>
                <div className="space-y-1">
                  <div className="h-3 bg-white/20 rounded-md" />
                  <div className="h-3 bg-white/20 rounded-md" />
                </div>
              </div>
              <div className="px-2 py-2 bg-white">
                <p className="text-xs font-bold text-gray-800 truncate">{template.name}</p>
                <p className="text-[10px] text-gray-400 truncate">{template.description}</p>
              </div>
              {activeFunnel.designTemplate === template.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-[#3E3EFF] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Structure Tab with Drag & Drop (kept for AI-managed structure, not rendered) ──
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function StructureTab({
  activeFunnel,
  activeFunnelIndex,
  reorderFunnelSections,
  handleToggleSection,
  language,
  isFetchingDesignSections,
}: {
  activeFunnel: any;
  activeFunnelIndex: number;
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => void;
  handleToggleSection: (id: string, enabled: boolean) => void;
  language: Language;
  isFetchingDesignSections: boolean;
}) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNode = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    dragNode.current = e.currentTarget;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      if (dragNode.current) dragNode.current.style.opacity = '0.4';
    }, 0);
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    setDragOverIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const sections = [...activeFunnel.sections];
      const [removed] = sections.splice(dragIndex, 1);
      sections.splice(dragOverIndex, 0, removed);
      const reordered = sections.map((s: FunnelSection, i: number) => ({ ...s, order: i }));
      reorderFunnelSections(activeFunnelIndex, reordered);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragNode.current = null;
  }, [dragIndex, dragOverIndex, activeFunnel.sections, activeFunnelIndex, reorderFunnelSections]);

  const hasDesignSections = activeFunnel.sections.some((s: FunnelSection) => !s.isStandard);

  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
        {dictionary["funnel-structure"]?.[language] ?? "ESTRUCTURA DEL EMBUDO"}
      </p>

      {isFetchingDesignSections ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={36} />
        </div>
      ) : !hasDesignSections ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4">
            <Image className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm text-gray-400 font-medium mb-1">Selecciona un diseño primero</p>
          <p className="text-xs text-gray-300">Las secciones se cargarán según el diseño elegido</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
          {activeFunnel.sections.map((section: FunnelSection, index: number) => {
            const isDragOver = dragOverIndex === index && dragIndex !== index;
            const isDesignSection = !section.isStandard;
            const IconComponent = sectionIcons[section.icon] || Settings;

            return (
              <div
                key={section.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                  isDragOver
                    ? 'border-[#3E3EFF] bg-[#F0F0FF] border-dashed'
                    : section.enabled
                      ? 'border-[#E2E8F0] bg-[#FAFAFF]'
                      : 'border-gray-100 bg-gray-50 opacity-60'
                }`}
              >
                <GripVertical className="w-4 h-4 text-gray-300 cursor-grab active:cursor-grabbing flex-shrink-0" />

                {/* Thumbnail for design sections, icon for standard */}
                {isDesignSection && section.url ? (
                  <div className={`w-8 h-10 rounded-lg overflow-hidden flex-shrink-0 border ${
                    section.enabled ? 'border-[#E2E8F0]' : 'border-gray-200'
                  }`}>
                    <img
                      src={section.url}
                      alt={section.name}
                      className={`w-full h-full object-cover ${!section.enabled ? 'grayscale' : ''}`}
                    />
                  </div>
                ) : (
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    section.enabled ? 'bg-[#EDE9FE]' : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-4 h-4 ${section.enabled ? 'text-[#6366F1]' : 'text-gray-400'}`} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium block truncate ${section.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                    {section.name}
                  </span>
                  {isDesignSection && section.sectionType && (
                    <span className="text-[10px] text-gray-400 truncate block">{section.sectionType}</span>
                  )}
                </div>

                <Toggle
                  enabled={section.enabled}
                  onChange={(enabled) => handleToggleSection(section.id, enabled)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Angle Tab ───────────────────────────────────────────────
function AngleTab({
  activeFunnel,
  salesAngles,
  handleToggleAngle,
  onUpdateAngleDescription,
  onSelectCustomAngle,
  language,
  isFetchingSalesAngleRecommendation,
}: {
  activeFunnel: any;
  salesAngles: SalesAngle[];
  handleToggleAngle: (angleId: string) => void;
  onUpdateAngleDescription: (angleId: string, newDescription: string) => void;
  onSelectCustomAngle: (name: string, description: string) => void;
  language: Language;
  isFetchingSalesAngleRecommendation: boolean;
}) {
  const iconMap = [Clock, Users, Shield, Zap, Leaf];

  // Edit mode state
  const [editingAngleId, setEditingAngleId] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState('');

  // Custom angle state
  const isCurrentlyCustom = activeFunnel.selectedAngles.length > 0 &&
    !salesAngles.some((a: SalesAngle) => a.id === activeFunnel.selectedAngles[0].name);

  const [showCustomForm, setShowCustomForm] = useState(isCurrentlyCustom);
  const [customAngleName, setCustomAngleName] = useState(
    isCurrentlyCustom ? activeFunnel.selectedAngles[0].name : ''
  );
  const [customAngleDescription, setCustomAngleDescription] = useState(
    isCurrentlyCustom ? activeFunnel.selectedAngles[0].description : ''
  );

  // Skeleton loading animation
  if (salesAngles.length === 0 || isFetchingSalesAngleRecommendation) {
    return (
      <div>
        {/* Loading indicator header */}
        <div className="flex items-center gap-2.5 mb-5 px-1">
          <div className="relative flex items-center justify-center w-8 h-8">
            <div className="absolute inset-0 rounded-full border-2 border-[#3E3EFF]/20 border-t-[#3E3EFF] animate-spin" />
            <Sparkles className="w-4 h-4 text-[#3E3EFF]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              {dictionary["loading-personalized-sales-angles"]?.[language] ?? "Cargando ángulos de venta personalizados..."}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {dictionary["generating-suggestions-with-ai"]?.[language] ?? "Generando sugerencias con IA ..."}
            </p>
          </div>
        </div>
        {/* Skeleton cards */}
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="w-full px-4 py-4 rounded-[20px] border-2 border-gray-100" style={{ opacity: 1 - i * 0.15 }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 skeleton-card flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded-md skeleton-card" style={{ width: `${120 + i * 20}px` }} />
                  <div className="h-3 bg-gray-50 rounded-md skeleton-card" style={{ width: `${180 + i * 15}px` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-gray-900 mb-4">
        {dictionary["select-sales-angle"]?.[language] ?? "Selecciona un ángulo de venta"}
      </h2>
      <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
        {/* Regular angles from AI */}
        {salesAngles.map((angle: SalesAngle, index: number) => {
          const isSelected = activeFunnel.selectedAngles.some((a: any) => a.name === angle.id);
          const isEditing = editingAngleId === angle.id;
          const Icon = angle.icon || iconMap[index % iconMap.length];

          return (
            <div
              key={angle.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (showCustomForm) {
                  setShowCustomForm(false);
                  setCustomAngleName('');
                  setCustomAngleDescription('');
                }
                handleToggleAngle(angle.id);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleToggleAngle(angle.id)}
              className={`w-full px-4 py-3 rounded-[20px] border-2 transition-all text-left cursor-pointer ${
                isSelected
                  ? 'border-[#3E3EFF] bg-[#F5F8FF]'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-[#3E3EFF] text-white' : 'bg-gray-100'
                }`}>
                  {isSelected ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                    {angle.title}
                  </p>
                  {/* Edit mode: inline textarea */}
                  {isSelected && isEditing ? (
                    <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        className="w-full text-xs text-gray-700 border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#3E3EFF] focus:border-[#3E3EFF]"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateAngleDescription(angle.id, editedDescription);
                            setEditingAngleId(null);
                          }}
                          className="px-3 py-1 text-xs font-medium text-white bg-[#3E3EFF] rounded-full hover:bg-[#2d3a7f] transition-colors"
                        >
                          {dictionary["save-changes"]?.[language] ?? "Guardar"}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingAngleId(null);
                          }}
                          className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          {dictionary.cancel?.[language] ?? "Cancelar"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {isSelected && angle.description && (
                        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                          {angle.description}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
              {/* Edit link — only when selected and NOT editing */}
              {isSelected && !isEditing && (
                <div className="mt-2 ml-[52px]">
                  <span
                    className="text-xs text-[#3E3EFF] font-medium flex items-center gap-1 cursor-pointer hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAngleId(angle.id);
                      setEditedDescription(angle.description);
                    }}
                  >
                    <Pencil className="w-3 h-3" />
                    {dictionary["edit-angle"]?.[language] ?? "Editar"}
                  </span>
                </div>
              )}
            </div>
          );
        })}

        {/* Custom Angle Card */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            if (!showCustomForm) {
              setShowCustomForm(true);
              // Deselect any regular angle
              if (activeFunnel.selectedAngles.length > 0 &&
                salesAngles.some((a: SalesAngle) => a.id === activeFunnel.selectedAngles[0].name)) {
                handleToggleAngle(activeFunnel.selectedAngles[0].name);
              }
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && !showCustomForm && setShowCustomForm(true)}
          className={`w-full px-4 py-3 rounded-[20px] border-2 transition-all text-left cursor-pointer ${
            showCustomForm
              ? 'border-[#3E3EFF] bg-[#F5F8FF]'
              : 'border-dashed border-gray-300 hover:border-gray-400 bg-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              showCustomForm ? 'bg-[#3E3EFF] text-white' : 'bg-gray-100'
            }`}>
              {showCustomForm ? (
                <Check className="w-5 h-5" />
              ) : (
                <Pencil className="w-5 h-5 text-gray-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${showCustomForm ? 'text-gray-900' : 'text-gray-500'}`}>
                {dictionary["custom-angle"]?.[language] ?? "Ángulo personalizado"}
              </p>
              {!showCustomForm && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {dictionary["create-your-own-approach"]?.[language] ?? "Crea tu propio enfoque."}
                </p>
              )}
            </div>
          </div>

          {showCustomForm && (
            <div className="mt-3 ml-[52px] space-y-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={customAngleName}
                onChange={(e) => setCustomAngleName(e.target.value)}
                placeholder={dictionary["custom-angle-name-placeholder"]?.[language] ?? "Nombre del ángulo"}
                className="w-full text-xs text-gray-700 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#3E3EFF] focus:border-[#3E3EFF]"
              />
              <textarea
                value={customAngleDescription}
                onChange={(e) => setCustomAngleDescription(e.target.value)}
                placeholder={dictionary["custom-angle-desc-placeholder"]?.[language] ?? "Describe tu enfoque de venta..."}
                className="w-full text-xs text-gray-700 border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#3E3EFF] focus:border-[#3E3EFF]"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!customAngleName.trim() || !customAngleDescription.trim()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectCustomAngle(customAngleName.trim(), customAngleDescription.trim());
                  }}
                  className="px-3 py-1 text-xs font-medium text-white bg-[#3E3EFF] rounded-full hover:bg-[#2d3a7f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {dictionary["save-changes"]?.[language] ?? "Guardar"}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCustomForm(false);
                    setCustomAngleName('');
                    setCustomAngleDescription('');
                  }}
                  className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  {dictionary.cancel?.[language] ?? "Cancelar"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Ads Tab ─────────────────────────────────────────────────
type AdStyleTab = 'all' | 'viral' | 'offer' | 'testimonials';
const AD_STYLE_TABS: AdStyleTab[] = ['all', 'viral', 'offer', 'testimonials'];
const MAX_AD_TEMPLATES = 20;

function AdsTab({
  activeFunnel,
  language,
  adTemplates,
  isFetchingAdTemplates,
  onToggleTemplate,
}: {
  activeFunnel: FunnelConfig;
  language: Language;
  adTemplates: AdStyle[];
  isFetchingAdTemplates: boolean;
  onToggleTemplate: (templateId: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<AdStyleTab>('all');
  const selectedTemplates = activeFunnel.selectedAdTemplates || [];
  const isMaxReached = selectedTemplates.length >= MAX_AD_TEMPLATES;

  const filteredTemplates = useMemo(() => {
    if (activeTab === 'all') return adTemplates;
    return adTemplates.filter(t => t.category === activeTab);
  }, [adTemplates, activeTab]);

  // Distribute templates into 3 columns round-robin for stable masonry layout
  const templateColumns = useMemo(() => {
    const cols: [AdStyle[], AdStyle[], AdStyle[]] = [[], [], []];
    filteredTemplates.forEach((t, i) => cols[i % 3].push(t));
    return cols;
  }, [filteredTemplates]);


  const getTabLabel = (tab: AdStyleTab): string => {
    const labels: Record<AdStyleTab, string> = {
      all: dictionary["all"]?.[language] ?? 'Todos',
      viral: dictionary["viral"]?.[language] ?? 'Viral',
      offer: dictionary["offer"]?.[language] ?? 'Oferta',
      testimonials: dictionary["testimonials"]?.[language] ?? 'Testimonios',
    };
    return labels[tab];
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            {dictionary["select-ad-templates-title"]?.[language] ?? "Selecciona hasta 20 plantillas de anuncios para este embudo"}
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            {dictionary["ad-templates-subtitle"]?.[language] ?? "A partir de cada plantilla seleccionada crearemos un anuncio para tu producto."}
          </p>
        </div>
        {selectedTemplates.length > 0 && (
          <span className="text-sm font-bold text-[#3E3EFF] whitespace-nowrap ml-4">
            {(dictionary["n-selected"]?.[language] ?? "{n} seleccionados").replace("{n}", String(selectedTemplates.length))}
          </span>
        )}
      </div>

      {/* Selection counter banner */}
      {isMaxReached && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2">
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex-shrink-0">!</div>
          <p className="text-xs text-amber-700 font-medium">
            {(dictionary["max-ad-templates-reached"]?.[language] ?? "Llegaste al máximo de {n} plantillas por embudo").replace("{n}", String(MAX_AD_TEMPLATES))}
          </p>
          <span className="ml-auto text-xs font-bold text-amber-700 tabular-nums">{selectedTemplates.length}/{MAX_AD_TEMPLATES}</span>
        </div>
      )}

      {/* Category tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide whitespace-nowrap pb-1">
        {AD_STYLE_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              activeTab === tab
                ? 'bg-[#1E1E2E] text-white'
                : 'bg-white border border-[#E2E8F0] text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {getTabLabel(tab)}
          </button>
        ))}
      </div>

      {/* Templates — 3 flex columns with scroll */}
      {isFetchingAdTemplates ? (
        <div className="max-h-[350px] overflow-y-auto pr-1">
          <div className="flex gap-3">
            {[0, 1, 2].map((col) => (
              <div key={col} className="flex-1 flex flex-col gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-gray-100 animate-pulse"
                    style={{ height: (col + i) % 3 === 0 ? '180px' : (col + i) % 3 === 1 ? '140px' : '160px' }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="max-h-[350px] overflow-y-auto pr-1 p-1 -m-1">
          <div className="flex gap-3 items-start">
            {templateColumns.map((col, colIdx) => (
              <div key={colIdx} className="flex-1 flex flex-col gap-3">
                {col.map((template) => {
                  const isSelected = selectedTemplates.includes(template.id);
                  const disabled = isMaxReached && !isSelected;

                  return (
                    <div
                      key={template.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => !disabled && onToggleTemplate(template.id)}
                      onKeyDown={(e) => e.key === 'Enter' && !disabled && onToggleTemplate(template.id)}
                      className={`relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-200 ${
                        isSelected
                          ? 'ring-2 ring-[#3E3EFF] ring-offset-2'
                          : disabled
                            ? 'opacity-40 cursor-not-allowed'
                            : 'hover:scale-[1.02]'
                      }`}
                    >
                      <LazyImage
                        src={template.coverImage}
                        alt={template.name}
                        className="w-full block"
                        wrapperClassName="w-full"
                      />
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#3E3EFF] rounded-full flex items-center justify-center z-10 shadow">
                          <Check className="h-3 w-3 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-32 text-gray-400">
          <p className="text-sm">No se encontraron plantillas</p>
        </div>
      )}
    </div>
  );
}
